import { assertNever, definedMap } from "@glideapps/ts-necessities";

import {
    ActionKind,
    InputOutputTables,
    PropertyDescription,
    TableColumn,
    isLinkTypeKind,
    MutatingScreenKind,
    ActionDescription,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { SerializedComponent } from "../../components/SerializedApp";
import { panic } from "../../Support";
import {
    PropertyDescriptor,
    ComponentBuilder,
    ComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { makeColumnProperty, makeEnumProperty } from "../components/property-utils";
import { BaseActionHandler, tokenForProperty } from "./base";
import { DescriptionToken } from "./action-handler";
import { makeTextPropertyDescriptor } from "../components/descriptor-utils";
import { LinkAppearance, makePushWebViewScreen, makeText } from "../builder-utils";
import { EnumPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { linkAppearances } from "../components/link";
import { SerializedElementBuilder } from "../serialized-component";
import { asString } from "../../computation-model/data";

export interface OpenWebViewActionDescription extends ActionDescription {
    readonly link: PropertyDescription | undefined;
    readonly appearance: PropertyDescription | undefined;
}

export const webViewAppearancePropertyHandler = new EnumPropertyHandler(
    { appearance: LinkAppearance.PageTitle },
    "Title",
    "Show as",
    linkAppearances,
    PropertySection.Design,
    "dropdown"
);

enum Target {
    Browser = "browser",
    WebView = "web-view",
}

interface OpenLinkActionDescription extends OpenWebViewActionDescription {
    readonly target: PropertyDescription | undefined;
}

export function makeLinkColumnPropertyDescriptor(
    mutatingScreenKind: MutatingScreenKind | undefined
): PropertyDescriptor {
    return makeTextPropertyDescriptor("link", "URL", "Enter URL", true, mutatingScreenKind, {
        preferredNames: ["link", "url"],
        preferredType: "uri",
        searchable: false,
        applyFormat: false,
        columnFirst: true,
    });
}

const targetPropertyHandler = new EnumPropertyHandler(
    { target: Target.Browser },
    "Target",
    "Target",
    [
        {
            label: "Browser",
            value: Target.Browser,
        },
        {
            label: "Web View",
            value: Target.WebView,
        },
    ],
    PropertySection.Options,
    "dropdown"
);

function getTarget(desc: OpenLinkActionDescription | undefined): Target {
    return definedMap(desc, d => targetPropertyHandler.getEnum(d)) ?? targetPropertyHandler.defaultCaseValue;
}

export function makeOpenWebViewAction(
    b: SerializedElementBuilder,
    desc: OpenWebViewActionDescription,
    url: SerializedComponent
): SerializedComponent {
    const appearance = webViewAppearancePropertyHandler.getEnum(desc);

    return b.let(
        [["link", url]],
        b.ifIsDefined(b.getVariable("link"), makePushWebViewScreen(b, b.getVariable("link"), appearance))
    );
}

export class OpenLinkWithArgsHandler extends BaseActionHandler<OpenLinkActionDescription> {
    public readonly kind = ActionKind.OpenLinkWithArgs;

    public readonly iconName = "link";

    public getDescriptor(
        desc: OpenLinkActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        const properties: PropertyDescriptor[] = [
            makeLinkColumnPropertyDescriptor(mutatingScreenKind),
            targetPropertyHandler,
        ];
        if (getTarget(desc) === Target.WebView) {
            properties.push(webViewAppearancePropertyHandler);
        }

        return {
            name: "Open Link",
            needsScreenContext: true,
            properties,
        };
    }

    public newDefaultActionDescription(c: TableColumn): OpenLinkActionDescription | undefined {
        if (!isLinkTypeKind(c.type.kind)) return undefined;
        return {
            kind: ActionKind.OpenLinkWithArgs,
            link: makeColumnProperty(c.name),
            target: makeEnumProperty(Target.Browser),
            appearance: undefined,
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: OpenLinkActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for Open Link action");
        }

        const url = makeText(builder, desc.link, tables.input, true, false, true, false, screenContext);
        if (url === undefined) return undefined;

        const target = getTarget(desc);
        if (target === Target.Browser) {
            return b.actionIfContextDefined(b.openLink(url));
        } else if (target === Target.WebView) {
            return makeOpenWebViewAction(b, desc, url);
        } else {
            return assertNever(target);
        }
    }

    public getTokenizedDescription(
        desc: OpenLinkActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const token = tokenForProperty(desc.link, tables, ccc);
        if (token === undefined) return undefined;
        return [token];
    }

    public inflate(ib: WireInflationBackend, desc: OpenLinkActionDescription): WireActionHydrator | undefined {
        const [linkGetter] = ib.getValueGetterForProperty(desc.link, false);

        return vp => {
            const link = asString(linkGetter(vp)).trim();
            if (link === "") return undefined;

            return async ab => {
                ab.actionCallbacks.openLink(link);
                return true;
            };
        };
    }
}
